import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  Add,
  AddCircle,
  ContentCopy,
  Delete,
  FlipCameraAndroid,
  MoreVert,
  PlayArrow,
  Search,
  Share,
  VolumeDown,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import LanguageSourceTarget from './LanguageSourceTarget'
import Vocabulary from './Vocabulary'

import './VocabularyPanel.less'
import { useAppDispatch, useAppSelector } from '../hooks'
import {
  deleteCurrentVocabulary,
  getCurrentCell,
  getCurrentRow,
  listMyVocabularies,
  newVocabulary,
  splitCurrentVocabulary,
  updateCurrentVocabulary,
} from '../Store/VocabulariesSlice'
import { toast } from 'react-toastify'
import CurrentVocabularyStatus from './CurrentVocabularyStatus'
import CurrentVocabularyLineCount from './CurrentVocabularyLineCount'
import { useNavigate } from 'react-router-dom'
import { useConfirm } from 'material-ui-confirm'
import { DisplayTool } from './VocabularyTools'
import VocabularyShareDialog from './VocabularyShareDialog'
import { performSearch } from '../Store/SearchSlice'
import { text_from_html } from '../Utilities/tools'
import { useVocabularyActions } from '../hooks/useVocabularyActions'

interface VocabularyPanelProps {
  onSelectVocabulary: (n: number) => void;
  onSearch: (params: { query: string, source: string, target: string }) => void;
}

// Language and tool constants
const DEFAULT_SOURCE_LANGUAGE = 'en'
const DEFAULT_TARGET_LANGUAGE = 'hu'
const TOOL_PRONOUNCE = 'pronounce'
const TOOL_COPY = 'copy'

const VocabularyPanel: React.FC<VocabularyPanelProps> = ({ onSelectVocabulary, onSearch }) => {
    const navigate = useNavigate()
    const panelRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const confirm = useConfirm()

    // Scrolling

    const [isScrolling, setIsScrolling] = useState(false)
    useEffect(() => {
      const handleScroll = () => {
        if (panelRef.current) {
          setIsScrolling(panelRef.current.scrollTop > 0)
        }
      }
      const panel = panelRef.current
      if (panel) {
        panel.addEventListener('scroll', handleScroll)
      }

      return () => {
        if (panel) {
          panel.removeEventListener('scroll', handleScroll)
        }
      }
    }, [])

    // List vocabularies

    const dispatch = useAppDispatch()

    const vocabularies = useAppSelector(
      (state) => state.vocabularies.vocabularies,
    )
    useEffect(() => {
      const loaded = vocabularies.length > 0
      if (!loaded) {
        dispatch(listMyVocabularies())
      }
    }, [vocabularies.length, dispatch])

    // Current vocabulary actions

    const currentVocabulary = useAppSelector(
      (state) => state.vocabularies.currentVocabulary,
    )
    const currentRow = useAppSelector(getCurrentRow)
    const currentCell = useAppSelector(getCurrentCell)
    const isVocabularyEmpty = !currentVocabulary?.rows.filter(r => r.left && r.right).length
    const vocabularyTitle = currentVocabulary?.title || ''

    // always keep input in sync with vocabularyTitle, except while focused
    const [vocabularyTitleInput, setVocabularyTitleInput] = useState(vocabularyTitle)
    const inputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
      // Only update if not focused
      if (document.activeElement !== inputRef.current) {
        setVocabularyTitleInput(vocabularyTitle)
      }
    }, [vocabularyTitle])
    const setVocabularyTitle = (title: string) => {
      dispatch(updateCurrentVocabulary({ title }))
    }
    const sourceLanguage = currentVocabulary?.left_language || DEFAULT_SOURCE_LANGUAGE
    const targetLanguage = currentVocabulary?.right_language || DEFAULT_TARGET_LANGUAGE
    const updateLanguages = async ({ source, target }: { source: string, target: string }) => {
      const data = { left_language: source, right_language: target }
      await dispatch(updateCurrentVocabulary(data))
    }

    // --- Handlers ---
    const handleNewVocabulary = useCallback(async () => {
      try {
        const response = await dispatch(newVocabulary())
        if (newVocabulary.fulfilled.match(response)) {
          const id = response.payload.id
          navigate(`?v=${id}&showSearch=false`)
        } else {
          toast.error(t('ErrorCreatingVocabulary'))
        }
      } catch (err) {
        toast.error(t('ErrorCreatingVocabulary'))
      }
    }, [dispatch, navigate, t])

  const { action } = useVocabularyActions(currentVocabulary, currentRow)

    // More menu
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const openMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    }
    const closeMoreMenu = () => {
      setAnchorEl(null)
    }
    // Which tool to display in the vocabulary...
    const [displayTools, setDisplayTools] = useState<DisplayTool>('')

    const handleSearch = useCallback(() => {
      if (!currentVocabulary || !currentCell) return
      const { rowIndex, column } = currentCell
      const query = text_from_html(currentVocabulary.rows[rowIndex][column])
      const [source, target] = (column === 'left') ? [sourceLanguage, targetLanguage] : [targetLanguage, sourceLanguage]
      dispatch(performSearch({ query, source, target }))
      onSearch({ query, source, target })
    }, [currentVocabulary, currentCell, dispatch, onSearch, sourceLanguage, targetLanguage])

    const handleLearnWords = () => {
      navigate(`/cards?v=${currentVocabulary?.id}`)
    }

    const handleDeleteCurrentVocabulary = useCallback(async () => {
      try {
        await confirm({ description: t('WarningPermanentAction') })
        const result = await dispatch(deleteCurrentVocabulary())
        if (deleteCurrentVocabulary.fulfilled.match(result)) {
          toast.success(t('VocabularyDeleted'))
          navigate('/?showSearch=false')
        } else {
          toast.error(t('ErrorDeletingVocabulary'))
        }
      } catch {
        // Do nothing on cancel
      }
    }, [confirm, dispatch, navigate, t])

    const splitVocabulary = useCallback(async () => {
      const position = currentRow?.index
      if (position === undefined) return
      try {
        await confirm({ description: t('WarningPermanentAction') })
        const result = await dispatch(splitCurrentVocabulary({ position }))
        if (splitCurrentVocabulary.fulfilled.match(result)) {
          toast.success(t('VocabularySplitInfo', { title: result.payload?.newVocabulary.title as string }))
        } else {
          toast.error(t('ErrorSplittingVocabulary'))
        }
      } catch {
        // Do nothing on cancel
      }
    }, [confirm, currentRow, dispatch, t])

    // Helper to wrap menu actions and close menu
    const menuAction = (fn: () => void | Promise<void>) => async () => {
      await fn()
      closeMoreMenu()
    }
    const [openShareDialog, setOpenShareDialog] = useState(false)

    const currentLeftLanguage = currentVocabulary?.left_language || ''

    const sortedVocabularies = useMemo(() => {
      return [...vocabularies].sort((a, b) => {
        if (a.left_language !== b.left_language) return (a.left_language === currentLeftLanguage) ? -1 : 1
        return a.created > b.created ? -1 : 1
      })
    }, [vocabularies, currentLeftLanguage])
    // Memoized handlers for toolbar actions
    const handleAddRow = () => action('addRow')
    const handleDeleteRow = () => action('deleteRow')
    const handleFlipRow = () => action('flipRow')
    const handleTogglePronounce = useCallback(() => setDisplayTools(displayTools !== TOOL_PRONOUNCE ? TOOL_PRONOUNCE : ''), [displayTools])
    const handleToggleCopy = useCallback(() => setDisplayTools(displayTools !== TOOL_COPY ? TOOL_COPY : ''), [displayTools])

    return (
      <div
        ref={panelRef}
        className={`vocabulary-panel ${isScrolling ? 'scrolling' : ''}`}
      >
        <VocabularyShareDialog
          open={openShareDialog}
          onClose={() => setOpenShareDialog(false)}
          vocabularyId={currentVocabulary?.id || 0} />
        <div className="container">
          <div className="vocabulary-toolbar">
            <Tooltip title={t('tooltip.Search')}>
              <IconButton onClick={handleSearch}>
                <Search />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('tooltip.Add')}>
              <IconButton onClick={handleAddRow}>
                <Add />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('tooltip.Delete')}>
              <IconButton onClick={handleDeleteRow}>
                <Delete />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('tooltip.Learn')}>
              <span>
                <IconButton onClick={handleLearnWords} disabled={isVocabularyEmpty} className="emphasized-button">
                  <PlayArrow />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('tooltip.More')}>
              <IconButton onClick={openMoreMenu}>
                <MoreVert />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMoreMenu}
            >
              <MenuItem onClick={menuAction(() => setOpenShareDialog(true))}>
                <ListItemIcon>
                  <Share fontSize="small" />
                </ListItemIcon>
                {t('tooltip.Share')}
              </MenuItem>
              <MenuItem onClick={menuAction(splitVocabulary)}>
                <ListItemIcon />
                {t('SplitVocabulary')}
              </MenuItem>
              <MenuItem onClick={menuAction(handleFlipRow)}>
                <ListItemIcon>
                  <FlipCameraAndroid fontSize="small" />
                </ListItemIcon>
                {t('tooltip.FlipRow')}
              </MenuItem>
              <MenuItem onClick={menuAction(handleTogglePronounce)}>
                <ListItemIcon>
                  <VolumeDown fontSize="small" />
                </ListItemIcon>
                {t('ShowHidePronunciations')}
              </MenuItem>
              <MenuItem onClick={menuAction(handleToggleCopy)}>
                <ListItemIcon>
                  <ContentCopy fontSize="small" />
                </ListItemIcon>
                {t('ShowHideCopyButtons')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={menuAction(handleDeleteCurrentVocabulary)}>
                <ListItemIcon />
                {t('DeleteVocabulary')}
              </MenuItem>
            </Menu>
            <div style={{ flexGrow: 1 }} />
            <CurrentVocabularyStatus />
            <CurrentVocabularyLineCount />
          </div>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <TextField
              inputRef={inputRef}
              value={vocabularyTitleInput}
              placeholder={t('VocabularyTitlePlaceholder')}
              onChange={(event) => setVocabularyTitleInput(event.target.value)}
              onBlur={() => setVocabularyTitle(vocabularyTitleInput)}
              sx={{
                '& .MuiInputBase-root': { paddingRight: 0 },
                flexGrow: 1,
              }}
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <LanguageSourceTarget
                        sourceLanguage={sourceLanguage}
                        targetLanguage={targetLanguage}
                        onChange={updateLanguages}
                      />
                    </InputAdornment>
                  </>
                ),
              }}
              data-testid="vocabulary-title-input"
            />
            <Select
              className="vocabulary-select"
              value={currentVocabulary?.id || ''}
              onChange={(e) => onSelectVocabulary(e.target.value as number)}
              renderValue={() => ''}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'right',
                },
              }}
              data-testid="vocabulary-select"
            >
              <MenuItem onClick={handleNewVocabulary}>
                <ListItemIcon>
                  <AddCircle />
                </ListItemIcon>
                {t('AddNewVocabulary')}
              </MenuItem>
              <Divider />
              {sortedVocabularies.map(({ id, title, left_language, right_language }) => (
                <MenuItem key={id} value={id}>
                  <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                    <span className="vocab-menu-title">{title || t('UntitledVocabulary')}</span>
                    <span>{left_language}→{right_language}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </div>
          <div style={{ marginBottom: '200px' }}>
            {currentVocabulary &&
              <Vocabulary
                vocabulary={currentVocabulary}
                displayTools={displayTools}
                readOnly={false}
              />}
          </div>
        </div>
      </div>
    )
}

export default VocabularyPanel
